import { useEffect, useRef } from "react";
import { useSavedDraft } from "./use-save-attempt";

/** Every mounted draft that names a scope, by that scope, each as the save of what it holds. */
const heldDrafts = new Map<string, Set<() => void>>();

/**
 * Save every draft in the scope that differs from what is stored.
 *
 * An act that must read the saved words (a lock, a confirmation) calls this
 * first. It does not wait for the field to be left: a draft whose last save was
 * refused is still in its field, and a clicked button is not always focused, so
 * no blur can be counted on to have sent it.
 */
export function flushCommittedDrafts(scope: string): void {
  for (const flush of heldDrafts.get(scope) ?? []) flush();
}

/**
 * A draft that follows the server while untouched, saves when the field is
 * left, and saves again on the way out.
 *
 * The last words typed are the ones most likely to be lost: unmounting a row
 * does not fire blur, and a debounce still counting when the attorney
 * navigates away discards what it was holding. Two fields wrote this ref dance
 * separately, and one of them wrote a second adoption rule with it.
 *
 * `commit` compares against the stored value rather than against the last
 * attempt, so a write that failed is retried rather than treated as saved;
 * `failed` says the last one was refused, and `retry` sends the words again.
 */
export function useCommittedDraft(
  stored: string,
  save: (value: string) => Promise<void>,
  /** Registers the draft for {@link flushCommittedDrafts} under this scope. */
  scope?: string,
) {
  const { draft, setDraft, send, failed } = useSavedDraft(stored, save);

  const commit = (value: string) => {
    if (value !== stored) send(value);
  };

  // Read through refs because the unmount effect runs once and would otherwise
  // close over the first render's words and the first render's save.
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  useEffect(
    () => () => {
      commitRef.current(draftRef.current);
    },
    [],
  );
  useEffect(() => {
    if (scope === undefined) return;
    const flush = () => {
      commitRef.current(draftRef.current);
    };
    const held = heldDrafts.get(scope) ?? new Set();
    heldDrafts.set(scope, held.add(flush));
    return () => {
      held.delete(flush);
      if (held.size === 0) heldDrafts.delete(scope);
    };
  }, [scope]);

  return {
    draft,
    setDraft,
    commit,
    failed,
    retry: () => {
      commit(draft);
    },
  };
}
